import { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X, ArrowLeft, ArrowRight, Check, ChevronRight,
  Building2, Stethoscope, FlaskConical, Search, Plus, FileCheck,
  PenLine, Upload, FileText, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/auth.store';
import { SignaturePad, type SignaturePadRef } from '@/components/SignaturePad';
import { getErrorMessage } from '@/lib/errors';

const FIRMA_ARCHIVO_MIMES = ['image/png', 'image/jpeg', 'application/pdf'];
const FIRMA_ARCHIVO_MAX_BYTES = 10 * 1024 * 1024; // 10MB, igual que el límite del backend

function fmtBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type TipoConsentimiento = 'clinica' | 'medico' | 'tratamiento';

interface Plantilla {
  id: string;
  nombre: string;
  tipo: TipoConsentimiento;
  contenido: string;
  activo: boolean;
}

interface WizardForm {
  plantillaId: string | null;
  tipo: TipoConsentimiento;
  titulo: string;
  contenido: string;
}

type StepId = 'plantilla' | 'contenido' | 'firma';
const STEPS: StepId[] = ['plantilla', 'contenido', 'firma'];
const STEP_LABEL: Record<StepId, string> = {
  plantilla: 'Plantilla',
  contenido: 'Contenido',
  firma:     'Firma',
};

const TIPO_INFO: Record<TipoConsentimiento, { label: string; icon: typeof Building2; cls: string }> = {
  clinica:     { label: 'Clínica',     icon: Building2,    cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  medico:      { label: 'Médico',      icon: Stethoscope,  cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  tratamiento: { label: 'Tratamiento', icon: FlaskConical,  cls: 'bg-teal-50 text-teal-700 border-teal-200' },
};

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  pacienteId: string;
  paciente?: any;
  onClose: () => void;
}

export function ConsentimientoWizard({ pacienteId, paciente, onClose }: Props) {
  const { usuario } = useAuthStore();
  const [stepIdx, setStepIdx] = useState(0);
  const [filterTipo, setFilterTipo] = useState<TipoConsentimiento | ''>('');
  const [search, setSearch] = useState('');
  const [custom, setCustom] = useState(false);
  const [form, setForm] = useState<WizardForm>({
    plantillaId: null,
    tipo: 'clinica',
    titulo: '',
    contenido: '',
  });
  const [savedId, setSavedId] = useState<string | null>(null);
  const [firmaModo, setFirmaModo] = useState<'dibujar' | 'archivo'>('dibujar');
  const [firmaArchivo, setFirmaArchivo] = useState<File | null>(null);
  const sigRef = useRef<SignaturePadRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: plantillas = [] } = useQuery<Plantilla[]>({
    queryKey: ['plantillas-consentimiento'],
    queryFn: () => api.get('/api/plantillas-consentimiento').then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post('/api/consentimientos', dto).then(r => r.data),
    onSuccess: (data) => { if (data?.id) setSavedId(data.id); },
  });

  const firmarMutation = useMutation({
    mutationFn: ({ id, firmaBase64 }: { id: string; firmaBase64: string }) =>
      api.patch(`/api/consentimientos/${id}/firmar`, { firmaBase64 }).then(r => r.data),
    onSuccess: () => { toast.success('Consentimiento firmado correctamente'); onClose(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar la firma')),
  });

  const firmarArchivoMutation = useMutation({
    mutationFn: ({ id, archivo }: { id: string; archivo: File }) => {
      const fd = new FormData();
      fd.append('file', archivo);
      return api.post(`/api/consentimientos/${id}/firmar-archivo`, fd).then(r => r.data);
    },
    onSuccess: () => { toast.success('Consentimiento firmado correctamente'); onClose(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al subir el documento firmado')),
  });

  const currentStep = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  function selectPlantilla(p: Plantilla) {
    setForm({ plantillaId: p.id, tipo: p.tipo, titulo: p.nombre, contenido: p.contenido });
    setCustom(false);
  }

  function enableCustom() {
    setForm(prev => ({ ...prev, plantillaId: null, titulo: '', contenido: '' }));
    setCustom(true);
  }

  const filtered = plantillas.filter(p => {
    if (!p.activo) return false;
    if (filterTipo && p.tipo !== filterTipo) return false;
    if (search && !p.nombre.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  function validateStep(): boolean {
    if (currentStep === 'plantilla' && !form.plantillaId && !custom) {
      toast.error('Selecciona una plantilla o elige "Personalizado"');
      return false;
    }
    if (currentStep === 'contenido' && !form.contenido.trim()) {
      toast.error('El contenido no puede estar vacío');
      return false;
    }
    if (currentStep === 'contenido' && !form.titulo.trim()) {
      toast.error('Escribe un título para este consentimiento');
      return false;
    }
    return true;
  }

  async function next() {
    if (!validateStep()) return;
    if (currentStep === 'contenido') {
      try {
        const dto = {
          pacienteId,
          medicoId:   usuario?.id,
          clinicaId:  usuario?.clinicaId,
          plantillaId: form.plantillaId,
          tipo:       form.tipo,
          titulo:     form.titulo,
          contenido:  form.contenido,
        };
        const created = await createMutation.mutateAsync(dto);
        setSavedId(created.id);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Error al guardar el consentimiento'));
        return;
      }
    }
    setStepIdx(i => Math.min(i + 1, STEPS.length - 1));
  }

  function prev() { setStepIdx(i => Math.max(i - 1, 0)); }

  async function handleFirmar() {
    const id = savedId;
    if (!id) { toast.error('Error interno: consentimiento no guardado'); return; }

    if (firmaModo === 'dibujar') {
      const firma = sigRef.current?.getBase64();
      if (!firma) { toast.error('Dibuja la firma antes de confirmar'); return; }
      firmarMutation.mutate({ id, firmaBase64: firma });
      return;
    }

    if (!firmaArchivo) { toast.error('Selecciona una imagen o PDF antes de confirmar'); return; }
    firmarArchivoMutation.mutate({ id, archivo: firmaArchivo });
  }

  function handleFirmaArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!FIRMA_ARCHIVO_MIMES.includes(f.type)) {
      toast.error('Formato no soportado: usa PNG, JPEG o PDF');
      return;
    }
    if (f.size > FIRMA_ARCHIVO_MAX_BYTES) {
      toast.error('El archivo excede el tamaño máximo permitido (10MB)');
      return;
    }
    setFirmaArchivo(f);
  }

  const toDisplayName = (p: any) =>
    [p?.nombre, p?.apellidoPaterno, p?.apellidoMaterno].filter(Boolean).join(' ');

  // ─── Step content ─────────────────────────────────────────────────────────

  function renderStep() {
    switch (currentStep) {
      case 'plantilla': return (
        <div className="space-y-4">
          {/* Filter tabs */}
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setFilterTipo('')}
              className={cn('text-xs px-2.5 py-1 rounded-full border transition-colors',
                filterTipo === '' ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-text-secondary hover:border-gray-400'
              )}>Todos</button>
            {(Object.entries(TIPO_INFO) as [TipoConsentimiento, typeof TIPO_INFO['clinica']][]).map(([key, info]) => (
              <button
                key={key}
                onClick={() => setFilterTipo(key)}
                className={cn('text-xs px-2.5 py-1 rounded-full border transition-colors',
                  filterTipo === key ? `${info.cls} font-semibold` : 'border-gray-200 text-text-secondary hover:border-gray-400'
                )}>
                {info.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar plantilla..."
              className={`${inputCls} pl-8`}
            />
          </div>

          {/* Template list */}
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-text-secondary py-6">Sin plantillas para este filtro</p>
            ) : filtered.map(p => {
              const isSelected = form.plantillaId === p.id && !custom;
              const info = TIPO_INFO[p.tipo];
              const Icon = info.icon;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectPlantilla(p)}
                  className={cn(
                    'flex items-start gap-3 w-full text-left p-3 rounded-xl border transition-all',
                    isSelected ? 'border-primary-400 bg-primary-50' : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                  )}>
                  <span className={cn('flex items-center justify-center w-7 h-7 rounded-lg border flex-shrink-0 mt-0.5', info.cls)}>
                    <Icon size={13} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary leading-tight">{p.nombre}</p>
                    <p className={cn('text-[10px] font-semibold mt-0.5', info.cls.split(' ')[1])}>{info.label}</p>
                  </div>
                  {isSelected && <Check size={14} className="text-primary-600 flex-shrink-0 mt-1" />}
                </button>
              );
            })}
          </div>

          {/* Custom option */}
          <button
            type="button"
            onClick={enableCustom}
            className={cn(
              'flex items-center gap-2 w-full p-3 rounded-xl border text-sm transition-all',
              custom ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-dashed border-gray-300 text-text-secondary hover:border-gray-400'
            )}>
            <Plus size={14} />
            Personalizado (escribir contenido)
            {custom && <Check size={14} className="ml-auto text-primary-600" />}
          </button>
        </div>
      );

      case 'contenido': return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Título del consentimiento *</label>
            <input
              className={inputCls}
              value={form.titulo}
              onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
              placeholder="Ej. Consentimiento informado para cirugía..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Alcance</label>
            <div className="flex gap-2">
              {(Object.entries(TIPO_INFO) as [TipoConsentimiento, typeof TIPO_INFO['clinica']][]).map(([key, info]) => (
                <button
                  key={key}
                  type="button"
                  disabled={!!form.plantillaId}
                  onClick={() => setForm(p => ({ ...p, tipo: key }))}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors',
                    form.tipo === key ? `${info.cls} font-semibold` : 'border-gray-200 text-text-secondary',
                    form.plantillaId ? 'opacity-60 cursor-not-allowed' : 'hover:border-gray-400'
                  )}>
                  {info.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Contenido *</label>
            <textarea
              rows={12}
              className={`${inputCls} resize-none font-mono text-xs leading-relaxed`}
              value={form.contenido}
              readOnly={!!form.plantillaId && !custom}
              onChange={e => setForm(p => ({ ...p, contenido: e.target.value }))}
              placeholder="Texto completo del consentimiento informado..."
            />
            {form.plantillaId && !custom && (
              <p className="text-[10px] text-text-secondary mt-1">Contenido de la plantilla (solo lectura)</p>
            )}
          </div>
        </div>
      );

      case 'firma': return (
        <div className="space-y-5">
          {/* Summary */}
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-1">
            <p className="text-xs text-text-secondary">Consentimiento</p>
            <p className="text-sm font-semibold text-text-primary">{form.titulo}</p>
            <div className="flex items-center gap-2 mt-1">
              {(() => {
                const info = TIPO_INFO[form.tipo];
                const Icon = info.icon;
                return (
                  <span className={cn('flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border', info.cls)}>
                    <Icon size={9} /> {info.label}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Patient info */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
            <p className="text-xs text-blue-700">
              El/la paciente <strong>{toDisplayName(paciente)}</strong> deberá firmar a continuación para confirmar que leyó y acepta el consentimiento.
            </p>
          </div>

          {/* Signature mode selector */}
          <div>
            <p className="text-xs font-medium text-text-secondary mb-2">Firma del paciente *</p>
            <div className="flex gap-1.5 mb-3">
              <button type="button" onClick={() => setFirmaModo('dibujar')}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  firmaModo === 'dibujar' ? 'bg-primary-50 text-primary-700 border-primary-200' : 'border-gray-200 text-text-secondary hover:border-gray-400'
                )}>
                <PenLine size={12} /> Dibujar firma
              </button>
              <button type="button" onClick={() => setFirmaModo('archivo')}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  firmaModo === 'archivo' ? 'bg-primary-50 text-primary-700 border-primary-200' : 'border-gray-200 text-text-secondary hover:border-gray-400'
                )}>
                <Upload size={12} /> Subir imagen o PDF
              </button>
            </div>

            {firmaModo === 'dibujar' ? (
              <SignaturePad ref={sigRef} height={160} />
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  className="hidden"
                  onChange={handleFirmaArchivo}
                />
                {firmaArchivo ? (
                  <div className="flex items-center gap-3 border border-gray-200 rounded-xl p-3">
                    <FileText size={18} className="text-primary-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-text-primary truncate">{firmaArchivo.name}</p>
                      <p className="text-[10px] text-text-secondary">{fmtBytes(firmaArchivo.size)}</p>
                    </div>
                    <button type="button" onClick={() => setFirmaArchivo(null)}
                      className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary hover:text-red-500 transition-colors flex-shrink-0">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl py-6 text-text-secondary hover:border-gray-400 hover:bg-gray-50 transition-colors">
                    <Upload size={18} />
                    <span className="text-xs">Documento firmado en papel, escaneado o foto (PNG, JPEG o PDF)</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  const isSaving  = createMutation.isPending;
  const isSigning = firmarMutation.isPending || firmarArchivoMutation.isPending;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h1 className="font-semibold text-text-primary text-sm flex items-center gap-2">
            <FileCheck size={15} className="text-primary-600" />
            Nuevo consentimiento · {toDisplayName(paciente)}
          </h1>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-0">
            {STEPS.map((step, i) => {
              const isActive    = i === stepIdx;
              const isCompleted = i < stepIdx;
              return (
                <div key={step} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => i < stepIdx && setStepIdx(i)}
                    disabled={i >= stepIdx}
                    className={cn('flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all',
                      isActive    ? 'text-primary-700 bg-primary-50 border border-primary-200' :
                      isCompleted ? 'text-green-700 cursor-pointer hover:bg-green-50'           :
                                    'text-text-secondary'
                    )}>
                    <span className={cn('w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                      isActive    ? 'bg-primary-600 text-white' :
                      isCompleted ? 'bg-green-500 text-white'   :
                                    'bg-gray-200 text-gray-500'
                    )}>
                      {isCompleted ? <Check size={9} /> : i + 1}
                    </span>
                    {STEP_LABEL[step]}
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight size={13} className="mx-1 text-gray-300 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <h2 className="text-base font-semibold text-text-primary mb-4">{STEP_LABEL[currentStep]}</h2>
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-white">
          <button onClick={prev} disabled={stepIdx === 0}
            className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30">
            <ArrowLeft size={14} /> Anterior
          </button>

          {isLast ? (
            <button
              onClick={handleFirmar}
              disabled={isSigning}
              className="flex items-center gap-1.5 text-sm bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors font-medium">
              <FileCheck size={14} />
              {isSigning ? 'Guardando...' : 'Confirmar y firmar'}
            </button>
          ) : (
            <button
              onClick={next}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors font-medium">
              {isSaving ? 'Guardando...' : 'Siguiente'} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
